/**
 * Customer (Kunden-grid) wire types — Story V10-J (web wiring, core screens).
 *
 * Shapes for the as-built GET /customers list endpoint (apps/api customers module).
 * snake_case field names match the DB/api conventions (see task.ts precedent).
 *
 * customer_state is the source table: one row per subject (person | firma) that
 * has been lifted into the customer lifecycle. Policy count / premium sum are
 * read-time aggregates over the subject's active policies.
 *
 * [Source: packages/db/src/schema/lifecycle.ts#customer_state]
 * [Source: apps/api/src/modules/customer-status/customer-status.constants.ts#CUSTOMER_STATUS]
 * [Source: docs/stories/v10-j-web-wiring-core.md]
 */

import type { PageMeta } from './pagination';

// ── Customer status (wire union) ─────────────────────────────────────────────

/**
 * The 6 customer lifecycle statuses as ASCII identifiers (DB enum values).
 * `gekuendigt` is the ASCII identifier; the UI label is `gekündigt`.
 * Mirrors apps/api customer-status.constants.ts CUSTOMER_STATUS (D6 state machine).
 */
export type CustomerStatusValue =
  | 'leer'
  | 'neu'
  | 'provisorisch'
  | 'aktiv'
  | 'gekuendigt'
  | 'inaktiv';

/** Ordered list of all valid customer status values (for @IsIn DTOs + UI filters). */
export const CUSTOMER_STATUS_WIRE_VALUES: readonly CustomerStatusValue[] = [
  'leer',
  'neu',
  'provisorisch',
  'aktiv',
  'gekuendigt',
  'inaktiv',
] as const;

/**
 * The final status. Shared by the SQL rules in customer-rules.ts, which cannot import the api's
 * CUSTOMER_STATUS.
 */
export const CUSTOMER_STATUS_INAKTIV = 'inaktiv' as const satisfies CustomerStatusValue;

// ── List item / response DTOs ─────────────────────────────────────────────────

/**
 * One Kunden-grid row (GET /customers).
 *
 * - name: person = "last_name first_name" (trimmed) | firma = firma.name.
 * - policy_count / premium_sum: aggregates over the subject's ACTIVE policies
 *   (current policy_version.premium_gross). premium_sum is a numeric string
 *   (like RenewalBoardItemDto.praemie) and is NULLED server-side when the
 *   caller's effective visibility denies `policen.praemie` (V10-E flagship).
 * - tenant_id is NEVER present (implicit from token — rule #1).
 */
export interface CustomerListItemDto {
  /** customer_state.id (the grid row identity). */
  customer_state_id: string;
  subject_type: 'person' | 'firma';
  /** person.id or firma.id (polymorphic). */
  subject_id: string;
  /** Resolved display name. Null if the subject row is missing/RLS-hidden. */
  name: string | null;
  /** firma.city (persons: null) — display-only. */
  city: string | null;
  /** firma.canton — the 2-letter Kanton code (persons: null). Display-only; pairs with `city`
   *  as the detail-header subtitle "Ort · Kanton" (G1-1). */
  canton: string | null;
  status: CustomerStatusValue;
  /** customer_state.customer_number (F5: assigned at creation, K-nnnnnn, immutable). */
  customer_number: string;
  /** Count of the subject's active policies. */
  policy_count: number;
  /** Sum of current-version premium_gross over active policies, as string. Null when visibility-denied or no policies. */
  premium_sum: string | null;
  created_at: string;
  /**
   * E4: when the CURRENT status started: newest customer_state_history row
   * whose from_status differs from to_status (a same-status re-set must not move
   * it); genesis counts. Null when no history row exists (rows predating
   * migration 0013): consumers render nothing and never fall back to created_at.
   */
  status_since: string | null;
  /**
   * P6 (7.14): the moment the nightly run made the customer inaktiv (ISO, UTC); null otherwise and on
   * legacy rows. The grid renders «inaktiv seit <Zurich day>» (inaktivSeitLabel). Optional on the wire.
   */
  inactivated_at?: string | null;
  /**
   * E1: the statuses this row may legally move to, straight off the api's
   * transition table. The UI renders these as the dropdown options and never
   * recomputes the rule (rule #2). Empty for terminal states (inaktiv).
   * An admin holding customer_status.override can still set anything.
   */
  allowed_transitions: CustomerStatusValue[];
  /**
   * MV (mail-only team visibility): the team whose members may see this client's MAIL.
   * null = everyone in the tenant may see it (the default; no setup needed).
   * The client RECORD, the Kundenliste, the search and the detail view are NEVER
   * restricted by this pointer - it is deliberately not customer_team_scope.
   * Written by PATCH /customers/:type/:id/mail-team.
   */
  mail_team_id: string | null;
  /**
   * Display name of the `mail_team_id` team, resolved in-query inside the tenant.
   * null when mail_team_id is null, and also when the team row no longer exists
   * (a dangling pointer keeps its id but has no name to render).
   */
  mail_team_name: string | null;
  /**
   * True when this record is open unconfirmed: it still waits for a decision
   * (CUSTOMER_UNCONFIRMED_OPEN_SQL). The column of the same name on firma and person means never
   * confirmed as a customer.
   */
  is_unconfirmed: boolean;
}

/**
 * GET /customers response envelope.
 *
 * Story V10-J2: keyset cursor pagination added (GET /tasks pattern).
 * `page.next_cursor` = the last kept row's created_at (ISO) when more rows
 * exist; null on the last page. Pass it back as `?cursor=` for the next page.
 *
 * Story GAP-B4: `page` retyped to the shared `PageMeta` (structurally identical
 * + the optional `total_count` on `?include_total=true` — additive, NOT a
 * repurpose).
 */
export interface CustomerListResponseDto {
  items: CustomerListItemDto[];
  page: PageMeta;
}

// ── Manual create (POST /customers) ───────────────────────────────────────────

/**
 * Discriminator for the manual create-customer wizard (POST /customers).
 * natuerliche_person → a standalone private client (person, no beziehung).
 * juristische_person → a company (firma; zefix_status stays 'pending' for enrichment).
 */
export type CustomerKundentyp = 'natuerliche_person' | 'juristische_person';

/**
 * POST /customers 201 response — the created subject's polymorphic key.
 * The web wizard navigates to the new customer's detail via `{subjectType}/{subjectId}`.
 * tenant_id is NEVER present (implicit from token — rule #1).
 */
export interface CreateCustomerResponseDto {
  subjectType: 'person' | 'firma';
  subjectId: string;
}

// ── Mail-team visibility (PATCH /customers/:type/:id/mail-team) ───────────────

/**
 * PATCH /customers/:type/:id/mail-team 200 response: the client's mail-team pointer
 * as it stands AFTER the write, in the same two fields the list row carries, so the
 * caller can patch its cached row instead of refetching the whole list.
 * tenant_id is NEVER present (implicit from the token, rule #1).
 */
export interface CustomerMailTeamDto {
  /** null = everyone in the tenant may see this client's mail. */
  mail_team_id: string | null;
  /** Display name of that team; null when mail_team_id is null. */
  mail_team_name: string | null;
}
